/**
 * Admin Activity Log.
 *
 * Read-only view of every tracked write in the system: create/update/delete
 * for audited models plus explicit business events (login, write-off,
 * attach-items, invoice merge/cancel, …). Filterable by action / model /
 * actor / object id.
 */
import { useEffect, useMemo, useState } from "react"
import { Search } from "lucide-react"

import { Button } from "@/components/ui/button"
import { DataTable, type DataTableColumn } from "@/components/ui/data-table"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { PageBody, PageHeader, PageShell } from "@/components/ui/page-shell"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useDateFormat } from "@/lib/dates"
import { AuditAPI, type AuditEntry } from "@/services/audit"
import { useToast } from "@/hooks/use-toast"

const ACTION_LABELS: Record<string, string> = {
  create: "Create",
  update: "Update",
  delete: "Soft delete",
  hard_delete: "Hard delete",
  restore: "Restore",
  login: "Login",
  login_failed: "Login failed",
  logout: "Logout",
  password_change: "Password change",
  invoice_attach: "Attach items",
  invoice_write_off: "Write off",
  invoice_merge: "Merge invoices",
  invoice_cancel: "Cancel invoice",
  invoice_email: "Invoice emailed",
  invoice_pdf: "PDF regenerated",
  export: "Export",
  other: "Other",
}

/**
 * Fields that are almost always noise in a human-facing diff (framework
 * timestamps, soft-delete bookkeeping, etc). Suppressed from the Changed
 * column so the meaningful fields (hours, rate, description…) stand out.
 */
const NOISY_FIELDS = new Set([
  "updated_at",
  "created_at",
  "last_modified_at",
  "deleted_at",
  "deleted_by_id",
  "last_login",
  "modified_by_id",
])

function humanizeField(name: string): string {
  const base = name.endsWith("_id") ? name.slice(0, -3) : name
  return base
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "∅"
  if (typeof v === "boolean") return v ? "yes" : "no"
  const s = typeof v === "string" ? v : JSON.stringify(v)
  return s.length > 60 ? `${s.slice(0, 60)}…` : s
}

/**
 * Turn a raw ``changes`` payload into "field: before → after" rows the
 * user can actually read. Covers the common ``{before, after, changed}``
 * shape used on update, plus the ``{after}`` / ``{before}`` shapes used
 * on create / delete.
 */
function describeChanges(entry: { action: string; changes?: Record<string, any> }) {
  const c = entry.changes ?? {}
  const out: Array<{ label: string; before?: string; after: string }> = []
  const changed: string[] | undefined = Array.isArray(c.changed) ? c.changed : undefined
  const before = (c.before ?? {}) as Record<string, unknown>
  const after = (c.after ?? {}) as Record<string, unknown>

  if (changed && (before || after)) {
    for (const k of changed) {
      if (NOISY_FIELDS.has(k)) continue
      out.push({
        label: humanizeField(k),
        before: formatValue(before[k]),
        after: formatValue(after[k]),
      })
    }
    return out
  }

  // Create / restore: only after-snapshot. Show a few highlight fields.
  if (Object.keys(after).length > 0) {
    for (const k of Object.keys(after)) {
      if (NOISY_FIELDS.has(k)) continue
      out.push({ label: humanizeField(k), after: formatValue(after[k]) })
    }
    return out
  }

  // Delete: before-snapshot only.
  if (Object.keys(before).length > 0) {
    for (const k of Object.keys(before)) {
      if (NOISY_FIELDS.has(k)) continue
      out.push({ label: humanizeField(k), after: formatValue(before[k]) })
    }
    return out
  }

  return out
}

const ACTION_CLASS: Record<string, string> = {
  create: "bg-emerald-100 text-emerald-800",
  update: "bg-sky-100 text-sky-800",
  delete: "bg-rose-100 text-rose-800",
  hard_delete: "bg-red-200 text-red-900",
  restore: "bg-indigo-100 text-indigo-800",
  login: "bg-slate-100 text-slate-700",
  login_failed: "bg-amber-100 text-amber-800",
  invoice_write_off: "bg-amber-100 text-amber-800",
  invoice_merge: "bg-purple-100 text-purple-800",
  invoice_cancel: "bg-red-100 text-red-800",
}

export function ActivityLog() {
  const { toast } = useToast()
  const { format } = useDateFormat()
  const [rows, setRows] = useState<AuditEntry[]>([])
  const [loading, setLoading] = useState(true)
  // "" is reserved by Radix Select for the cleared/placeholder state,
  // so we use a sentinel string for the "no filter" option and translate
  // it to undefined when calling the API.
  const ALL_ACTIONS = "__all__"
  const [action, setAction] = useState<string>(ALL_ACTIONS)
  const [query, setQuery] = useState<string>("")

  async function load() {
    setLoading(true)
    try {
      const data = await AuditAPI.list({
        action: action && action !== ALL_ACTIONS ? action : undefined,
        q: query.trim() || undefined,
        limit: 200,
      })
      setRows(data.entries)
    } catch (e: any) {
      toast({
        title: "Could not load activity",
        description: e?.message ?? "Admin access required.",
        variant: "destructive",
      })
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action])

  // Debounce free-text search so each keystroke doesn't hit the API.
  useEffect(() => {
    const t = setTimeout(() => {
      load()
    }, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const columns: DataTableColumn<AuditEntry>[] = useMemo(
    () => [
      {
        key: "at",
        header: "When",
        renderCell: (r) => (
          <span className="tabular-nums text-xs">
            {format(new Date(r.at))}{" "}
            <span className="text-muted-foreground">
              {new Date(r.at).toLocaleTimeString()}
            </span>
          </span>
        ),
      },
      {
        key: "action",
        header: "Action",
        primary: true,
        renderCell: (r) => (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ACTION_CLASS[r.action] ?? "bg-slate-100 text-slate-700"}`}
          >
            {ACTION_LABELS[r.action] ?? r.action}
          </span>
        ),
      },
      {
        key: "actor",
        header: "Actor",
        renderCell: (r) => r.actor_label || <span className="text-muted-foreground">system</span>,
      },
      {
        key: "target",
        header: "Target",
        renderCell: (r) =>
          r.model ? (
            <span className="text-sm">
              <span className="text-muted-foreground">{r.model}</span>{" "}
              <span className="font-medium">
                {r.object_repr || `#${r.object_id}`}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: "changed",
        header: "Changed",
        renderCell: (r) => {
          const diff = describeChanges(r)
          if (diff.length === 0) return <span className="text-muted-foreground">—</span>
          const shown = diff.slice(0, 3)
          const extra = diff.length - shown.length
          return (
            <div className="flex flex-col gap-0.5 text-xs">
              {shown.map((d, i) => (
                <span key={i} className="text-muted-foreground">
                  <span className="font-medium text-foreground">{d.label}:</span>{" "}
                  {d.before !== undefined ? (
                    <>
                      <span className="line-through opacity-70">{d.before}</span>
                      <span className="mx-1">→</span>
                    </>
                  ) : null}
                  <span className="text-foreground">{d.after}</span>
                </span>
              ))}
              {extra > 0 ? (
                <span className="text-muted-foreground">+ {extra} more</span>
              ) : null}
            </div>
          )
        },
        hiddenOnMobile: true,
      },
      {
        key: "path",
        header: "Request",
        renderCell: (r) => (
          <span className="font-mono text-xs text-muted-foreground">
            {r.request_method} {r.request_path}
          </span>
        ),
        hiddenOnMobile: true,
      },
      {
        key: "action_btn",
        header: "",
        align: "right",
        renderCell: (r) => {
          if (r.action !== "delete" || !r.model || !r.object_id || !r.app_label) return null
          return (
            <Button
              size="sm"
              variant="outline"
              onClick={async (e) => {
                e.stopPropagation()
                try {
                  await AuditAPI.restore(r.app_label!, r.model!, r.object_id)
                  toast({
                    title: "Restored",
                    description: `${r.model} #${r.object_id} is back.`,
                  })
                  load()
                } catch (err: any) {
                  toast({
                    title: "Restore failed",
                    description: err?.message ?? "Check logs.",
                    variant: "destructive",
                  })
                }
              }}
            >
              Restore
            </Button>
          )
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [format],
  )

  return (
    <PageShell>
      <PageHeader
        title="Activity log"
        description="Append-only record of every tracked write. Admins only."
        actions={<Button variant="outline" onClick={load}>Refresh</Button>}
      />
      <PageBody>
        <div className="grid grid-cols-1 gap-3 rounded-md border bg-card p-3 sm:grid-cols-[1fr_200px_auto]">
          <div className="space-y-1">
            <Label>Search</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Who, what, or where — e.g. “John Doe”, “Invoice 2024-42”, “/api/timesheets”"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Action</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger>
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_ACTIONS}>All actions</SelectItem>
                {Object.entries(ACTION_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              variant="ghost"
              onClick={() => {
                setAction(ALL_ACTIONS)
                setQuery("")
              }}
            >
              Clear
            </Button>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          loading={loading}
          empty="No matching entries."
        />
      </PageBody>
    </PageShell>
  )
}
